import { IconButton, Typography } from '@mui/material';
import { ArrowLeft, X } from 'lucide-react';
import * as S from './styles';
import type { ActivityLogPanelProps } from './types';

/**
 * The activity-log shell: title row with an optional back button, a close button,
 * and a scrollable body slot.
 *
 * Container-agnostic on purpose — the request flow mounts it in a width-animated
 * aside, data-analysis in a persistent drawer, and a modal pane is possible too.
 * It owns no i18n (`shared` may not reach for a namespace), so both button labels
 * arrive as props.
 *
 * Compose it:
 *   <ActivityLogPanel title={t('activity_log')} closeLabel={t('close')} onClose={…}>
 *     <ActivityTimeline entries={entries} emptyLabel={t('activity_log_empty')} />
 *   </ActivityLogPanel>
 */
export function ActivityLogPanel({
  title,
  closeLabel,
  onClose,
  onBack,
  backLabel,
  children,
  className,
  'data-testid': dataTestId = 'activity-log-panel',
}: ActivityLogPanelProps) {
  return (
    <S.PanelRoot className={className} data-testid={dataTestId}>
      <S.PanelHeader>
        <S.PanelHeaderLeft>
          {onBack ? (
            <IconButton size='small' aria-label={backLabel} onClick={onBack} edge='start'>
              <ArrowLeft size={16} />
            </IconButton>
          ) : null}
          <Typography color='text.secondary' fontWeight={600} variant='caption'>
            {title}
          </Typography>
        </S.PanelHeaderLeft>
        <IconButton size='small' aria-label={closeLabel} onClick={onClose} edge='end'>
          <X size={16} />
        </IconButton>
      </S.PanelHeader>
      <S.PanelBody>{children}</S.PanelBody>
    </S.PanelRoot>
  );
}
